import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { GuestContext } from '../../../types/Guest'

interface GuestFarewellState {
  active: boolean
  reason: 'ended' | 'abandoned' | 'removed' | null
}

export interface GuestArrivalNotification {
  id: string
  guestName: string
  guestCompany: string
  bookingId: string
  meetingTitle: string
  guestSessionId: string
}

interface GuestState {
  /** Non-null when the current session is a guest */
  guestContext: GuestContext | null
  /** The meeting token used to join */
  meetingToken: string | null
  /** Meeting info returned by /api/guest/validate-token */
  meetingInfo: {
    meetingTitle?: string
    startTime?: string
    endTime?: string
    hostName?: string
    roomName?: string
    guestEmail?: string
    companyId?: string
    companyName?: string
    customRoomId?: string
    virtualRoomId?: string
    receptionRoomId?: string
    hasSevvAccount?: boolean
    /** Registration Mode is Closed (SC-443): the farewell offers the waitlist. */
    signupClosed?: boolean
  } | null
  /** Farewell state for meeting end / abandonment */
  farewellState: GuestFarewellState
  /** Waiting message shown in reception */
  waitingMessage: string | null
  /** Guest arrival notifications for internal users */
  guestArrivals: GuestArrivalNotification[]
  /** Persistent map of guest sessionId → bookingId (survives toast dismissal) */
  guestSessionBookings: Record<string, string>
}

const initialState: GuestState = {
  guestContext: null,
  meetingToken: null,
  meetingInfo: null,
  farewellState: {
    active: false,
    reason: null,
  },
  waitingMessage: null,
  guestArrivals: [],
  guestSessionBookings: {},
}

const guestSlice = createSlice({
  name: 'guest',
  initialState,
  reducers: {
    setGuestContext(state, action: PayloadAction<GuestContext | null>) {
      state.guestContext = action.payload
    },
    setMeetingToken(state, action: PayloadAction<string | null>) {
      state.meetingToken = action.payload
    },
    setMeetingInfo(state, action: PayloadAction<GuestState['meetingInfo']>) {
      state.meetingInfo = action.payload
    },
    setGuestFarewell(state, action: PayloadAction<{ reason: GuestFarewellState['reason'] }>) {
      state.farewellState = { active: true, reason: action.payload.reason }
    },
    clearGuestFarewell(state) {
      state.farewellState = { active: false, reason: null }
    },
    setGuestWaitingMessage(state, action: PayloadAction<string | null>) {
      state.waitingMessage = action.payload
    },
    addGuestArrival(state, action: PayloadAction<Omit<GuestArrivalNotification, 'id'>>) {
      state.guestArrivals.push({ id: `ga_${Date.now()}`, ...action.payload })
    },
    removeGuestArrival(state, action: PayloadAction<string>) {
      state.guestArrivals = state.guestArrivals.filter(a => a.id !== action.payload)
    },
    removeGuestArrivalsByBooking(state, action: PayloadAction<string>) {
      state.guestArrivals = state.guestArrivals.filter(a => a.bookingId !== action.payload)
    },
    trackGuestSession(state, action: PayloadAction<{ guestSessionId: string; bookingId: string }>) {
      state.guestSessionBookings[action.payload.guestSessionId] = action.payload.bookingId
    },
    untrackGuestSession(state, action: PayloadAction<string>) {
      delete state.guestSessionBookings[action.payload]
    },
    resetGuestState() {
      return initialState
    },
  },
})

export const {
  setGuestContext,
  setMeetingToken,
  setMeetingInfo,
  setGuestFarewell,
  clearGuestFarewell,
  setGuestWaitingMessage,
  addGuestArrival,
  removeGuestArrival,
  removeGuestArrivalsByBooking,
  trackGuestSession,
  untrackGuestSession,
  resetGuestState,
} = guestSlice.actions

export default guestSlice.reducer

/** True while this tab is an external guest's session (set by GuestJoinPage before the game loads). */
export function isGuestSession(state: { guest?: { guestContext: GuestContext | null } }): boolean {
  return !!state.guest?.guestContext
}
